import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { get, post } from "../api/client";
import { useAuth } from "../state";
import { Navbar } from "../components/navbar";
import type { ShopListResponse, ShopItem, PurchaseResponse, Ninja } from "../types";

interface Toast {
  message: string;
  type: "success" | "error";
  fading: boolean;
}

function ShopItemCard({
  item,
  balance,
  onBuy,
}: {
  item: ShopItem;
  balance: number;
  onBuy: (item: ShopItem) => void;
}) {
  const { t } = useTranslation();
  const affordable = balance >= item.price;
  const available = item.isAvailable;
  const canBuy = affordable && available;

  return (
    <div className={`shop-item ${!canBuy ? "disabled" : ""}`}>
      <div className="shop-item-icon">
        <span className="item-placeholder">{item.name.charAt(0)}</span>
      </div>
      <div className="shop-item-details">
        <h3 className="shop-item-name">{item.name}</h3>
        <p className="shop-item-description">{item.description}</p>
      </div>
      <div className="shop-item-footer">
        <span className="shop-item-price">{t("common.bux", { amount: item.price })}</span>
        <button
          className={`btn btn-buy ${!canBuy ? "btn-disabled" : ""}`}
          disabled={!canBuy}
          onClick={() => onBuy(item)}
        >
          {!available
            ? t("shop.unavailable")
            : !affordable
              ? t("shop.notEnoughBux")
              : t("shop.buy")}
        </button>
      </div>
    </div>
  );
}

function PurchaseModal({
  item,
  balance,
  onCancel,
  onConfirm,
  processing,
}: {
  item: ShopItem;
  balance: number;
  onCancel: () => void;
  onConfirm: () => void;
  processing: boolean;
}) {
  const { t } = useTranslation();

  // Escape closes the modal, but never while the purchase request is in flight —
  // same rule as the overlay and Cancel button below.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !processing) onCancel();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [processing, onCancel]);

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !processing) onCancel();
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-modal-title"
      >
        <h2 id="purchase-modal-title">{t("shop.confirmTitle")}</h2>
        <p>
          <Trans
            i18nKey="shop.confirmBody"
            values={{ item: item.name, price: item.price }}
            components={{ b: <strong /> }}
          />
        </p>
        <p className="modal-balance">
          {t("shop.balancePreview", {
            before: balance,
            after: balance - item.price,
          })}
        </p>
        <div className="modal-actions">
          <button className="btn btn-secondary" disabled={processing} onClick={onCancel}>
            {t("common.cancel")}
          </button>
          <button className="btn btn-primary" disabled={processing} onClick={onConfirm}>
            {processing ? t("shop.processing") : t("common.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ShopPage() {
  const { t } = useTranslation();
  const { facilityId, studentId, currentNinja, setCurrentNinja } = useAuth();

  const [items, setItems] = useState<ShopItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [balance, setBalance] = useState(currentNinja?.currentBalance ?? 0);
  const [pendingItem, setPendingItem] = useState<ShopItem | null>(null);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type, fading: false });
  }, []);

  // Match the old toast lifecycle: visible 3s, then a 300ms fade-out.
  useEffect(() => {
    if (!toast || toast.fading) return;
    const fadeTimer = setTimeout(
      () => setToast((t) => (t ? { ...t, fading: true } : t)),
      3000,
    );
    return () => clearTimeout(fadeTimer);
  }, [toast]);
  useEffect(() => {
    if (!toast?.fading) return;
    const removeTimer = setTimeout(() => setToast(null), 300);
    return () => clearTimeout(removeTimer);
  }, [toast]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Fetch ninja (for balance) and shop items
      const [ninjaRes, shopRes] = await Promise.all([
        currentNinja
          ? Promise.resolve({ data: currentNinja, error: undefined })
          : get<Ninja>(`/facilities/${facilityId}/ninjas/${studentId}`),
        get<ShopListResponse>(`/facilities/${facilityId}/shop`),
      ]);
      if (cancelled) return;

      if (ninjaRes.error || shopRes.error || !ninjaRes.data || !shopRes.data) {
        setLoadError(true);
        return;
      }

      setCurrentNinja(ninjaRes.data);
      setBalance(ninjaRes.data.currentBalance);
      setItems(
        shopRes.data.items
          .filter((item) => item.isAvailable)
          .sort((a, b) => a.price - b.price),
      );
    }

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, studentId]);

  async function confirmPurchase() {
    if (!pendingItem) return;
    setProcessing(true);

    const res = await post<PurchaseResponse>(
      `/facilities/${facilityId}/ninjas/${studentId}/purchases`,
      { shopItemId: pendingItem.id },
    );
    setProcessing(false);

    if (res.error || !res.data) {
      showToast(t("shop.purchaseFailed", { error: res.error }), "error");
      setPendingItem(null);
      return;
    }

    const newBalance = res.data.newBalance;
    setBalance(newBalance);
    if (currentNinja) {
      setCurrentNinja({ ...currentNinja, currentBalance: newBalance });
    }

    // Refresh shop items so availability and button states stay accurate.
    const shopRes = await get<ShopListResponse>(`/facilities/${facilityId}/shop`);
    if (shopRes.data) {
      setItems(
        shopRes.data.items
          .filter((item) => item.isAvailable)
          .sort((a, b) => a.price - b.price),
      );
    }

    const itemName = pendingItem.name;
    setPendingItem(null);
    showToast(t("shop.purchaseSuccess", { item: itemName }), "success");
  }

  return (
    <>
      <Navbar balance={balance} />
      <main className="shop-page">
        {loadError ? (
          <div className="error">{t("shop.loadFailed")}</div>
        ) : items === null ? (
          <div className="loading">{t("shop.loadingShop")}</div>
        ) : (
          <div className="shop-container">
            <div className="shop">
              <header className="shop-header">
                <h1>{t("shop.title")}</h1>
                <div className="shop-balance">
                  <span className="shop-balance-label">{t("shop.yourBalance")}</span>
                  <span className="shop-balance-amount">
                    {t("common.bux", { amount: balance })}
                  </span>
                </div>
              </header>

              <div className="shop-grid">
                {items.length > 0 ? (
                  items.map((item) => (
                    <ShopItemCard
                      key={item.id}
                      item={item}
                      balance={balance}
                      onBuy={setPendingItem}
                    />
                  ))
                ) : (
                  <div className="empty">{t("shop.noItems")}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
      {pendingItem && (
        <PurchaseModal
          item={pendingItem}
          balance={balance}
          processing={processing}
          onCancel={() => setPendingItem(null)}
          onConfirm={confirmPurchase}
        />
      )}
      {toast && (
        <div
          className={`toast toast-${toast.type} ${toast.fading ? "fade-out" : ""}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}
    </>
  );
}
